import {
  ChevronLeft,
  ChevronRight,
  Download,
  ExternalLink,
  Maximize2,
  RotateCw,
  ZoomIn,
  ZoomOut,
} from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { Button } from '~/components/ui/button';
import { Dialog, DialogContent, DialogTitle } from '~/components/ui/dialog';
import { cn } from '~/utils/cn';
import { triggerBrowserDownload } from '~/utils/download-file';

const MIN_SCALE = 1;
const MAX_SCALE = 8;
const SCALE_STEP = 0.5;

interface Props {
  /** Image URLs to page through. */
  images: string[];
  /** Index to open on; also the controlled starting slide. */
  index: number;
  onIndexChange: (index: number) => void;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Used for the download filename stem and the accessible dialog title. */
  title?: string | undefined;
  /** `data-testid` for the dialog; toolbar, nav and thumbnails derive from it. */
  testId?: string;
}

/** Filename for the download: the URL's own basename when it has a sane one,
 *  else a slug of the product title plus the 1-based slide number. */
function downloadName(url: string, title: string | undefined, position: number): string {
  const fromUrl = url.split('?')[0]?.split('/').pop();
  if (fromUrl && /\.(jpe?g|png|webp|gif|avif)$/i.test(fromUrl)) return fromUrl;
  const slug =
    title
      ?.toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '')
      .slice(0, 60) ?? '';
  // An all-punctuation title slugs to '' — `??` wouldn't catch that, hence the
  // explicit empty check.
  return `${slug === '' ? 'image' : slug}-${String(position)}.jpg`;
}

/**
 * Full-screen image lightbox: zoom (buttons, wheel, double-click), drag-to-pan
 * while zoomed, rotate, download, open-in-new-tab, and prev/next paging with a
 * thumbnail strip. Built on the Dialog primitive — no lightbox dependency.
 */
export function ImageViewer({
  images,
  index,
  onIndexChange,
  open,
  onOpenChange,
  title,
  testId = 'image-viewer',
}: Props) {
  const [scale, setScale] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [isDownloading, setIsDownloading] = useState(false);
  // Drag origin; null when not panning. A ref (not state) so pointermove doesn't
  // re-render on every pixel.
  const dragRef = useRef<{ x: number; y: number; ox: number; oy: number } | null>(null);

  const current = images.at(index) ?? '';
  const isZoomed = scale > MIN_SCALE;

  /** Any change of slide starts from a clean transform — carrying zoom/pan over
   *  leaves the next image parked off-screen. */
  const resetTransform = useCallback(() => {
    setScale(1);
    setRotation(0);
    setOffset({ x: 0, y: 0 });
  }, []);

  useEffect(() => {
    resetTransform();
  }, [index, open, resetTransform]);

  const go = useCallback(
    (delta: number) => {
      if (images.length < 2) return;
      // Wrap around so paging never dead-ends at either edge.
      onIndexChange((index + delta + images.length) % images.length);
    },
    [images.length, index, onIndexChange],
  );

  const zoomBy = useCallback((delta: number) => {
    setScale((s) => {
      const next = Math.min(MAX_SCALE, Math.max(MIN_SCALE, s + delta));
      // Snapping back to 1:1 must also recentre, or the image stays offset.
      if (next === MIN_SCALE) setOffset({ x: 0, y: 0 });
      return next;
    });
  }, []);

  // Keyboard shortcuts. Dialog already handles Escape.
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'ArrowRight') go(1);
      else if (e.key === 'ArrowLeft') go(-1);
      else if (e.key === '+' || e.key === '=') zoomBy(SCALE_STEP);
      else if (e.key === '-') zoomBy(-SCALE_STEP);
      else if (e.key === '0') resetTransform();
      else if (e.key.toLowerCase() === 'r') setRotation((r) => (r + 90) % 360);
      else return;
      e.preventDefault();
    }
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
    };
  }, [open, go, zoomBy, resetTransform]);

  /** Fetches the image and saves it via a blob so the file lands with a real
   *  name. Cross-origin images that refuse CORS fall back to a new tab, which
   *  is the most the browser will allow without a proxy. */
  async function download() {
    if (!current) return;
    setIsDownloading(true);
    try {
      const res = await fetch(current, { mode: 'cors' });
      if (!res.ok) throw new Error(`HTTP ${String(res.status)}`);
      triggerBrowserDownload(await res.blob(), downloadName(current, title, index + 1));
    } catch {
      window.open(current, '_blank', 'noopener,noreferrer');
    } finally {
      setIsDownloading(false);
    }
  }

  function onPointerDown(e: React.PointerEvent) {
    if (!isZoomed) return;
    dragRef.current = { x: e.clientX, y: e.clientY, ox: offset.x, oy: offset.y };
    e.currentTarget.setPointerCapture(e.pointerId);
  }

  function onPointerMove(e: React.PointerEvent) {
    const d = dragRef.current;
    if (!d) return;
    setOffset({ x: d.ox + (e.clientX - d.x), y: d.oy + (e.clientY - d.y) });
  }

  function onPointerUp(e: React.PointerEvent) {
    dragRef.current = null;
    if (e.currentTarget.hasPointerCapture(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid={testId}
        className="flex h-[92vh] max-w-[95vw] flex-col gap-0 overflow-hidden p-0 sm:max-w-[95vw]"
        onWheel={(e) => {
          // Radix locks body scroll while open, so the wheel is ours to use.
          zoomBy(e.deltaY < 0 ? SCALE_STEP : -SCALE_STEP);
        }}
      >
        {/* Toolbar. `pr-12` keeps the controls clear of Dialog's own close button. */}
        <div className="flex shrink-0 items-center justify-between gap-3 border-b px-4 py-3 pr-12">
          <div className="min-w-0">
            <DialogTitle data-testid={`${testId}-title`} className="truncate text-sm font-semibold">
              {title ?? 'Product image'}
            </DialogTitle>
            {images.length > 1 ? (
              <p data-testid={`${testId}-position`} className="text-muted-foreground text-xs">
                {index + 1} of {images.length}
              </p>
            ) : null}
          </div>

          <div className="flex shrink-0 items-center gap-1">
            <span
              data-testid={`${testId}-zoom-level`}
              className="text-muted-foreground w-12 text-center text-xs tabular-nums"
            >
              {Math.round(scale * 100)}%
            </span>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => {
                zoomBy(-SCALE_STEP);
              }}
              disabled={scale <= MIN_SCALE}
              aria-label="Zoom out"
              data-testid={`${testId}-zoom-out-button`}
              title="Zoom out (-)"
              className={cn(scale <= MIN_SCALE ? 'cursor-not-allowed' : 'cursor-pointer')}
            >
              <ZoomOut className="size-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => {
                zoomBy(SCALE_STEP);
              }}
              disabled={scale >= MAX_SCALE}
              aria-label="Zoom in"
              data-testid={`${testId}-zoom-in-button`}
              title="Zoom in (+)"
              className={cn(scale >= MAX_SCALE ? 'cursor-not-allowed' : 'cursor-pointer')}
            >
              <ZoomIn className="size-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => {
                setRotation((r) => (r + 90) % 360);
              }}
              aria-label="Rotate"
              data-testid={`${testId}-rotate-button`}
              title="Rotate (R)"
              className="cursor-pointer"
            >
              <RotateCw className="size-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={resetTransform}
              aria-label="Reset view"
              data-testid={`${testId}-reset-button`}
              title="Reset view (0)"
              className="cursor-pointer"
            >
              <Maximize2 className="size-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => {
                void download();
              }}
              isLoading={isDownloading}
              aria-label="Download image"
              data-testid={`${testId}-download-button`}
              title="Download image"
              className={cn(isDownloading ? 'cursor-not-allowed' : 'cursor-pointer')}
            >
              {isDownloading ? null : <Download className="size-4" />}
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => {
                window.open(current, '_blank', 'noopener,noreferrer');
              }}
              aria-label="Open original in new tab"
              data-testid={`${testId}-open-original-button`}
              title="Open original in new tab"
              className="cursor-pointer"
            >
              <ExternalLink className="size-4" />
            </Button>
          </div>
        </div>

        {/* Stage */}
        <div
          data-testid={`${testId}-stage`}
          className="relative min-h-0 flex-1 overflow-hidden bg-neutral-900"
        >
          <div
            data-testid={`${testId}-pan-surface`}
            className={cn(
              'flex h-full w-full items-center justify-center',
              isZoomed ? 'cursor-grab active:cursor-grabbing' : 'cursor-zoom-in',
            )}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
            onDoubleClick={() => {
              if (isZoomed) resetTransform();
              else setScale(2);
            }}
          >
            {current ? (
              <img
                data-testid={`${testId}-image`}
                src={current}
                alt={title ?? `Image ${String(index + 1)}`}
                draggable={false}
                className="max-h-full max-w-full object-contain select-none"
                style={{
                  transform: `translate(${String(offset.x)}px, ${String(offset.y)}px) scale(${String(scale)}) rotate(${String(rotation)}deg)`,
                  // No transition while dragging, or the image lags the cursor.
                  transition: dragRef.current ? 'none' : 'transform 150ms ease-out',
                }}
              />
            ) : (
              <p data-testid={`${testId}-empty`} className="text-sm text-neutral-400">
                No image available.
              </p>
            )}
          </div>

          {images.length > 1 ? (
            <>
              <Button
                variant="outline"
                size="icon"
                onClick={() => {
                  go(-1);
                }}
                aria-label="Previous image"
                data-testid={`${testId}-prev-button`}
                title="Previous image (←)"
                className="text-foreground absolute top-1/2 left-4 -translate-y-1/2 cursor-pointer rounded-full border-0 bg-white/90 shadow-sm hover:bg-white"
              >
                <ChevronLeft className="h-5 w-5" />
              </Button>
              <Button
                variant="outline"
                size="icon"
                onClick={() => {
                  go(1);
                }}
                aria-label="Next image"
                data-testid={`${testId}-next-button`}
                title="Next image (→)"
                className="text-foreground absolute top-1/2 right-4 -translate-y-1/2 cursor-pointer rounded-full border-0 bg-white/90 shadow-sm hover:bg-white"
              >
                <ChevronRight className="h-5 w-5" />
              </Button>
            </>
          ) : null}
        </div>

        {/* Thumbnail strip */}
        {images.length > 1 ? (
          <div
            data-testid={`${testId}-thumbnails`}
            className="flex shrink-0 gap-2 overflow-x-auto border-t p-3"
          >
            {images.map((src, i) => (
              <Button
                key={`${src}-${String(i)}`}
                variant="ghost"
                size="icon"
                onClick={() => {
                  onIndexChange(i);
                }}
                aria-label={`View image ${String(i + 1)}`}
                data-testid={`${testId}-thumbnail-${String(i)}`}
                aria-current={i === index}
                className={cn(
                  'size-14 shrink-0 cursor-pointer overflow-hidden rounded-lg border-2 p-0 transition-colors',
                  i === index ? 'border-primary' : 'border-transparent hover:border-neutral-300',
                )}
              >
                <img src={src} alt="" draggable={false} className="h-full w-full object-cover" />
              </Button>
            ))}
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
